import { getOrderItems, getOrderProductRating } from 'analytics/eventDefinitions'
import { getTypeaheadItemType } from 'lib/search/typeaheadUtils'

export interface OptimizelyEvent {
  type: 'event'
  eventName: string
  tags?: any
}

export function saveToTrip(): OptimizelyEvent {
  return {
    eventName: 'save-to-trip',
    type: 'event',
  }
}

export function clickFlightSearchWithRecentData(): OptimizelyEvent {
  return {
    eventName: 'click-flight-search-with-recent-data',
    type: 'event',
  }
}

export function purchaseClickEvent(order: App.Order): OptimizelyEvent {
  const orderItems = getOrderItems(order)
  const orderProductRating = getOrderProductRating(orderItems, order.total)

  return {
    type: 'event',
    eventName: 'transaction',
    tags: {
      revenue: order.total * 100,
      value: orderProductRating,
    },
  }
}

export function auPurchaseClickEvent(order: App.Order): OptimizelyEvent {
  const orderItems = getOrderItems(order)
  const orderProductRating = getOrderProductRating(orderItems, order.total)

  return {
    type: 'event',
    eventName: 'transaction-au',
    tags: {
      revenue: order.total * 100,
      value: orderProductRating,
    },
  }
}

export function addToCartAccommodationEvent(
): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'add-to-cart-accommodation',
  }
}

export function addToCartWithFlightsEvent(
): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'add-to-cart-with-flights',
  }
}

export function addToCartHotelOnlyEvent(
): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'add-to-cart-hotel-only',
  }
}

export function addToCartNumberOfNights(numberOfNights: number): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'add-to-cart-number-of-nights',
    tags: {
      numberOfNights,
    },
  }
}

export function successfulSignupEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'signup-complete',
  }
}

export function searchOfferImpression(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-offer-impression',
  }
}

export enum SearchResultType {
  All = 'all',
  Flash = 'flash',
  LPC = 'lpc',
  Bedbank = 'lpp',
  Hidden = 'hidden'
}

export function searchResultCount(type: SearchResultType, count: number): OptimizelyEvent {
  return {
    type: 'event',
    eventName: `search-result-${type}-count`,
    tags: {
      value: count,
    },
  }
}

export function searchClickCruiseOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-cruise-offer',
  }
}

export function searchClickCruiseSpecialOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-cruise-special-offer',
  }
}

export function searchClickCruiseNonSpecialOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-cruise-non-special-offer',
  }
}

export function searchClickPosition(pos: number): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-position',
    tags: {
      value: pos,
    },
  }
}

export function searchClickOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-offer',
  }
}

export function searchApplyFilter(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-apply-filter',
  }
}

export function searchClickFlashOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-flash-offer',
  }
}

export function searchClickLpcOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-lpc-offer',
  }
}

export function searchClickBedbankOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-bedbank-offer',
  }
}

export function searchClickBedbankOfferAnytimeWithSell(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-bedbank-offer-anytime-with-sell',
  }
}

export function purchaseLpcOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-lpc-offer',
  }
}

export function clickTourOfferEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-tour-offer',
  }
}

/*
 * This is a view tour checkout event, rather than an add to cart event.
 * This will be updated in the future to reflect that.
*/
export function addToCartTourEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'add-to-cart-tour',
  }
}

export function purchaseTourOfferEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-tour-offer',
  }
}

export function purchaseHotelWithFlightsEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-hotel-with-flights',
  }
}

export function purchaseFlightEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-flight',
  }
}

export function purchaseHotelEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-hotel',
  }
}

export function purchaseHotelOnlyEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-hotel-only',
  }
}

export function purchaseCruiseOfferEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'purchase-cruise-offer',
  }
}

export function clickZeroSearchResultOffer(hasNearbyOffer: boolean): OptimizelyEvent {
  return {
    type: 'event',
    eventName: hasNearbyOffer ? 'click-zero-search-result-offer-nearby' : 'click-zero-search-result-offer-no-nearby',
  }
}

export function shareOfferEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'share-offer',
  }
}

export function shareOfferClickedEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'share-offer-clicked',
  }
}

export function interactDuration(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'interact-duration',
  }
}

export function searchClickTypeahead(pos: number): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'search-click-typeahead',
    tags: {
      value: pos,
    },
  }
}

export function searchClickTypeaheadItem(item: App.SearchItem, pos: number): OptimizelyEvent {
  const type = getTypeaheadItemType(item)
  return {
    type: 'event',
    eventName: `search-click-typeahead-${type}`,
    tags: {
      value: pos,
    },
  }
}

export function insurancePurchaseEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'insurance-purchase',
  }
}

export function bookingProtectionPurchaseEvent(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'booking-protection-purchase',
  }
}

export function clickBroadSearchLocation(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-broad-search-location',
  }
}

export function clickAnyOffer(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-any-offer',
  }
}

export function clickHeroFinderOffer(offerId: string): OptimizelyEvent {
  return {
    type: 'event',
    eventName: `click-hero-finder-offer-${offerId}`,
  }
}

export function addCustomTripItem(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'add-custom-trip-item',
  }
}

export function clickSearchFlightDeal(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-search-flight-deal',
  }
}

export function applyPromoCodeOrGiftCard(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'apply-promo-code-or-gift-card',
  }
}

export function clickTypeaheadListItem(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-typeahead-list-item',
  }
}

export function clickTypeaheadTourListItem(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-typeahead-list-item-tour',
  }
}

export function signUpDetailsShown(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'sign-up-details',
  }
}

// LERE
export function clickTourOfferPageRecommendation(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-tour-offer-page-recommendation',
  }
}

export function viewTourOfferPageRecommendation(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'view-tour-offer-page-recommendation',
  }
}

export function clickGraphRecommendation(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'click-graph-recommendation-on-offer-page',
  }
}

export function viewGraphRecommendation(): OptimizelyEvent {
  return {
    type: 'event',
    eventName: 'view-graph-recommendation-on-offer-page',
  }
}
